import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from 'components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { Icon, IconName } from '../menu-icon/menu-icon';
import { rightIconWithModalProps, SidebarMenuItemProps } from 'models/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../components/ui/tooltip';
import { useTheme } from 'components/core/theme-provider';

export const SidebarMenuItemComponent: React.FC<SidebarMenuItemProps> = ({
  item,
  showText,
  onClick,
  className,
  CustomAction,
  disabled
}) => {
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const { pathname } = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { theme } = useTheme();
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTextOverflowing, setIsTextOverflowing] = useState(false);
  const translatedName = t(item.name);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        const isOverflowing = textRef.current.scrollWidth > textRef.current.clientWidth;
        setIsTextOverflowing(isOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [showText, translatedName]);

  const isParentActive =
    hasChildren && item.children?.some((child) => pathname.startsWith(child.path));
  const isActive = pathname.startsWith(item.path) || isParentActive;

  useEffect(() => {
    if (isParentActive && !isOpen) {
      setIsOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, isParentActive]);

  const strokeWidth = 2.2;

  const rightIconWithModal = ({ iconName, options, callback }: rightIconWithModalProps) => {
    // If there's a direct callback and no options, use a simple button
    if (callback && (!options || options.length === 0)) {
      return (
        <button
          className="ml-1 p-1 rounded-md hover:bg-muted-foreground/10"
          onClick={(e) => {
            e.stopPropagation();
            callback();
          }}
        >
          <Icon
            name={iconName}
            size={18}
            strokeWidth={strokeWidth}
            className={`${isActive || isHovered ? theme === 'dark' ? 'text-primary-200' : 'text-primary-600' : 'text-high-emphasis group-hover:text-primary'}`}
          />
        </button>
      );
    }

    // Otherwise, use dropdown menu
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="ml-1 p-1 rounded-md hover:bg-muted-foreground/10"
            onClick={(e) => e.stopPropagation()}
          >
            <Icon
              name={iconName}
              size={18}
              strokeWidth={strokeWidth}
              className={`${isActive || isHovered ? theme === 'dark' ? 'text-primary-200' : 'text-primary-600' : 'text-high-emphasis group-hover:text-primary'}`}
            />
          </button>
        </DropdownMenuTrigger>
        {options && options.length > 0 && (
          <DropdownMenuContent side="right" align="end" className="w-48">
            {options.map((option) => {

              if (option.type === 'submenu') {
                return (
                  <DropdownMenuSub key={option.id}>
                    <DropdownMenuSubTrigger
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-2"
                      disabled={option.isDisabled}
                    >
                      <Icon
                        name={option.icon as IconName}
                        size={16}
                        strokeWidth={strokeWidth}
                        className={`${isActive ? theme === 'dark' ? 'text-primary-200' : 'text-primary-600' : 'text-high-emphasis'}`}
                      />
                      <span>{option.name}</span>
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      {option.subOptions?.map((subOption) => (
                        <DropdownMenuItem
                          key={subOption.id}
                          className="px-1"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!subOption?.isDisabled && subOption?.callback) {
                              subOption.callback();
                            }
                          }}
                          disabled={subOption?.isDisabled}
                        >
                          <Icon
                            name={subOption.icon as IconName}
                            size={16}
                            strokeWidth={strokeWidth}
                            className={`${isActive ? theme === 'dark' ? 'text-primary-200' : 'text-primary-600' : 'text-high-emphasis'} mr-2`}
                          />
                          <span>{subOption.name}</span>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                );
              }

              return (
                <DropdownMenuItem
                  key={option.id}
                  onClick={option.callback}
                  className="flex items-center gap-2"
                >
                  <Icon
                    name={option.icon as IconName}
                    size={16}
                    strokeWidth={strokeWidth}
                    className={`${isActive ? theme === 'dark' ? 'text-primary-200' : 'text-primary-600' : 'text-high-emphasis'}`}
                  />
                  <span>{option.name}</span>
                </DropdownMenuItem>
              )
            })}
          </DropdownMenuContent>
        )}
      </DropdownMenu>
    );
  };

  const renderIcon = (iconName: IconName | undefined) => {
    if (!iconName) return null;

    return (
      <div className="flex items-center justify-center w-4 h-4">
        <Icon
          name={iconName}
          size={16}
          strokeWidth={strokeWidth}
          className="text-sidebar-foreground"
        />
      </div>
    );
  };

  const renderImageIcon = (image: string | undefined) => {
    if (!image) return null;

    return (
      <div className="flex items-center justify-center w-4 h-4 p-1 rounded-full bg-primary-light">
        <img src={image} alt="icon" className="w-4 h-4 object-contain rounded-full" />
      </div>
    );
  };

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  if (hasChildren) {
    return (
      <Collapsible className="group/collapsible" open={isOpen} onOpenChange={setIsOpen}>
        <SidebarMenuItem>
          <CollapsibleTrigger asChild>
            <SidebarMenuButton className='disabled:cursor-not-allowed' onClick={handleClick} disabled={disabled}>
              <div className="flex items-center justify-start w-full">
                {renderIcon(item.icon as IconName)}
                <span
                  className={`ml-3 truncate ${!showText && 'hidden'} ${isActive ? theme === 'dark' ? 'text-primary-200' : 'text-primary-600' : 'text-high-emphasis'} text-[14px] font-normal leading-[22px]`}
                >
                  {t(item.name)}
                </span>
                {showText && (
                  <ChevronRight
                    strokeWidth={strokeWidth}
                    className={`${isActive ? 'text-primary' : 'text-high-emphasis'} ml-auto h-5 w-5 transition-transform group-data-[state=open]/collapsible:rotate-90`}
                  />
                )}
              </div>
            </SidebarMenuButton>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <SidebarMenuSub>
              {item.children?.map((child) => {
                // Check if current path starts with child path for nested routes
                const isChildActive = pathname.startsWith(child.path);
                return (
                  <SidebarMenuSubItem key={child.id}>
                    <SidebarMenuSubButton asChild className={isChildActive ? 'bg-surface' : ''}>
                      <Link
                        to={child.path}
                        className="flex items-center w-full"
                        onClick={handleClick}
                      >
                        {renderIcon(child.icon as IconName)}
                        <span
                          className={`ml-3 truncate ${!showText && 'hidden'} ${isChildActive ? 'text-primary' : 'text-high-emphasis'} text-[14px] font-normal leading-[22px]`}
                        >
                          {child.name}
                        </span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          </CollapsibleContent>
        </SidebarMenuItem>
      </Collapsible>
    );
  }

  return (
    <SidebarMenuItem
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <SidebarMenuButton
        disabled={disabled}
        className={`h-10 disabled:cursor-not-allowed disabled:[&_*]:pointer-events-none disabled:[&_*]:cursor-not-allowed ${isActive ? 'bg-gradient-to-r from-[rgba(58,74,68,0.3)] via-[rgba(111,141,130,0.15)] via-[14.007%] to-[rgba(162,206,190,0.03)] to-[29.73%] border-l-2 border-l-[#526860] !p-0' : `${theme === 'dark' ? 'hover:bg-[rgba(82,104,96,0.15)]' : 'hover:bg-primary-25'} !p-0`} `}
      >
        <div className="flex items-center justify-between w-full">
          <div className="w-full">
            <Link to={item.path} className="block w-full h-full" onClick={handleClick}>
              <div className={`${className} flex items-center justify-start group-data-[collapsible=icon]:justify-center w-full gap-3 ${isActive ? 'py-3 px-[10px]' : 'py-2 px-3'} group-data-[collapsible=icon]:px-2`}>
                {item.imageIcon
                  ? renderImageIcon(item.imageIcon)
                  : renderIcon(item.icon as IconName)}
                {isTextOverflowing ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        ref={textRef}
                        className={`truncate ${!showText && 'hidden'} ${isActive ? 'text-[#816c51]' : 'text-sidebar-foreground'} text-[14px] font-medium leading-[22px]`}
                      >
                        {t(item.name)}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs text-gray-50">{t(item.name)}</p>
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <span
                    ref={textRef}
                    className={`truncate ${!showText && 'hidden'} text-sidebar-foreground text-[14px] font-medium leading-[22px]`}
                  >
                    {t(item.name)}
                  </span>
                )}
              </div>
            </Link>
          </div>
          {/* <Ellipsis className="ml-1 shrink-0" /> */}
          {CustomAction && (
            <div
              className={`absolute right-0 top-0 bottom-0 flex items-center pr-1 transition-opacity duration-200 ${isHovered || isActive ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            >
              {CustomAction}
            </div>
          )}
          {item?.rightIcon && (
            <div
              className={`absolute right-0 top-0 bottom-0 flex items-center pr-2.5 transition-opacity duration-200 ${isHovered || isActive ? 'opacity-100 visible' : 'opacity-0 invisible'} group-data-[collapsible=icon]:hidden`}
            >
              {rightIconWithModal(item.rightIcon)}
            </div>
          )}
        </div>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
};
